import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import '@css/index.scss'
import Footer from "@public/components/Footer";
import Header from "@components/Header";
import { api } from '@public/ulits/api';
import usePageLang from '@public/hooks/usePageLang';
import { t } from '@/locales';
import { getImage } from "@public/ulits";
import { GlobalOutlined, PhoneOutlined, MailOutlined, LeftOutlined } from "@ant-design/icons";
import { Spin } from 'antd';

export default () => {
    const { id } = useParams();
    const [emp, setEmp] = useState(null);
    const [loading, setLoading] = useState(true);
    const { pick } = usePageLang();

    useEffect(() => {
        setLoading(true);
        api.getEmployeeDetail(id)
            .then(res => {
                if (res && res.data) setEmp(res.data);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) {
        return (
            <div className={`page-index`}>
                <div className="w-full">
                    <Header bgColor="#0a1628" />
                    <div className="flex justify-center py-40">
                        <Spin size="large" />
                    </div>
                    <Footer />
                </div>
            </div>
        );
    }

    if (!emp) {
        return (
            <div className={`page-index`}>
                <div className="w-full">
                    <Header />
                    <div className="lg:w-[70%] lg:px-0 px-5 mx-auto lg:pt-[140px] pt-[90px] pb-[66px] text-center text-white">
                        <h1 className="text-2xl">{t('employee.notFound')}</h1>
                        <Link to="/employee" className="text-gold mt-4 inline-block">{t('employee.backToAgents')}</Link>
                    </div>
                    <Footer />
                </div>
            </div>
        );
    }

    const name = pick(emp.name_en, emp.name_zh);
    const title = pick(emp.title_en, emp.title_zh);
    const about = pick(emp.about_en, emp.about_zh);
    const hasDetails = emp.languages || emp.phone || emp.email || emp.license;

    return (
        <div className={`page-index`}>
            <div className="w-full">
                <Header bgColor="#0a1628" />
                <div className="lg:w-[70%] lg:px-0 px-5 mx-auto lg:pt-[140px] pt-[90px] lg:pb-[66px] pb-6 text-white">
                    <Link to="/employee" className="inline-flex items-center gap-2 text-[rgba(255,255,255,0.6)] hover:text-gold mb-10 transition-colors">
                        <LeftOutlined /> {t('employee.backToAgents')}
                    </Link>

                    {/* Section 1: Personal Profile */}
                    <div className="profile-card bg-[rgba(255,255,255,0.04)] rounded-2xl px-8 lg:px-12 pb-8 lg:pb-12 text-center mb-16
                        border border-[rgba(255,255,255,0.06)] hover:border-[rgba(255,255,255,0.15)]
                        hover:bg-[rgba(255,255,255,0.07)] hover:-translate-y-1
                        transition-all duration-300 mt-20 lg:mt-24 overflow-visible max-w-md mx-auto">
                        {emp.avatar ? (
                            <div className="lg:h-[110px] h-[90px] flex justify-center">
                                <img className="lg:w-44 lg:h-44 w-36 h-36 rounded-full object-cover
                                    border-[3px] border-gold-border shadow-gold-light
                                    -translate-y-1/2"
                                    src={emp.avatar} alt={name} />
                            </div>
                        ) : (
                            <div className="lg:h-[20px] h-[10px]"></div>
                        )}
                        <p className="text-gold text-sm tracking-[0.2em] uppercase mt-2 mb-1">{t('employee.personalProfile')}</p>
                        <h1 className="lg:text-[44px] text-3xl font-bold">{name}</h1>
                        {emp.license && (
                            <p className="text-[rgba(255,255,255,0.5)] mt-2 text-base">{t('employee.license')} {emp.license}</p>
                        )}
                        {title && (
                            <p className="text-gold text-lg mt-1 font-medium">{title}</p>
                        )}
                        {(emp.languages || emp.phone || emp.email) && (
                            <div className="flex justify-center flex-wrap gap-x-6 gap-y-2 mt-5 text-[rgba(255,255,255,0.7)] text-sm">
                                {emp.languages && <span className="flex items-center gap-2"><GlobalOutlined className="text-gold" /> {emp.languages}</span>}
                                {emp.phone && <span className="flex items-center gap-2"><PhoneOutlined className="text-gold" /> <a href={`tel:${emp.phone}`} className="hover:text-gold transition-colors">{emp.phone}</a></span>}
                                {emp.email && <span className="flex items-center gap-2"><MailOutlined className="text-gold" /> <a href={`mailto:${emp.email}`} className="hover:text-gold transition-colors">{emp.email}</a></span>}
                            </div>
                        )}
                    </div>

                    {/* Section 2: About Me */}
                    {about && (
                    <div className="mb-16 text-center">
                        <h2 className="lg:text-[36px] text-2xl font-bold mb-6">
                            {t('employee.aboutMe').split(' ').map((w, i) => i === 1 ? <span key={i} className="text-gold">{w}</span> : w + ' ')}
                        </h2>
                        <p lang="en" className="text-[rgba(255,255,255,0.7)] lg:text-lg text-base leading-relaxed lg:leading-loose text-justify text-hyphens">
                            {about}
                        </p>
                    </div>
                    )}

                    {/* Section 3: My Details */}
                    {hasDetails && (
                    <div className="text-center">
                        <h2 className="lg:text-[36px] text-2xl font-bold mb-6">
                            {t('employee.myDetails').split(' ').map((w, i) => i === 1 ? <span key={i} className="text-gold">{w}</span> : w + ' ')}
                        </h2>
                        <div className="grid lg:grid-cols-2 gap-4 text-left">
                            {emp.languages && (
                            <div className="flex items-center gap-4 bg-[rgba(255,255,255,0.04)] rounded-xl p-5 border border-[rgba(255,255,255,0.06)]">
                                <div className="w-12 h-12 rounded-xl bg-gold-bg flex items-center justify-center shrink-0">
                                    <GlobalOutlined className="text-gold text-xl" />
                                </div>
                                <div>
                                    <p className="text-[rgba(255,255,255,0.4)] text-xs uppercase tracking-wider">{t('employee.languages')}</p>
                                    <p className="text-white text-sm mt-0.5">{emp.languages}</p>
                                </div>
                            </div>
                            )}
                            {emp.phone && (
                            <div className="flex items-center gap-4 bg-[rgba(255,255,255,0.04)] rounded-xl p-5 border border-[rgba(255,255,255,0.06)]">
                                <div className="w-12 h-12 rounded-xl bg-gold-bg flex items-center justify-center shrink-0">
                                    <PhoneOutlined className="text-gold text-xl" />
                                </div>
                                <div>
                                    <p className="text-[rgba(255,255,255,0.4)] text-xs uppercase tracking-wider">{t('employee.phone')}</p>
                                    <a href={`tel:${emp.phone}`} className="text-white text-sm mt-0.5 block hover:text-gold transition-colors">{emp.phone}</a>
                                </div>
                            </div>
                            )}
                            {emp.email && (
                            <div className="flex items-center gap-4 bg-[rgba(255,255,255,0.04)] rounded-xl p-5 border border-[rgba(255,255,255,0.06)]">
                                <div className="w-12 h-12 rounded-xl bg-gold-bg flex items-center justify-center shrink-0">
                                    <MailOutlined className="text-gold text-xl" />
                                </div>
                                <div>
                                    <p className="text-[rgba(255,255,255,0.4)] text-xs uppercase tracking-wider">{t('employee.email')}</p>
                                    <a href={`mailto:${emp.email}`} className="text-white text-sm mt-0.5 block hover:text-gold transition-colors">{emp.email}</a>
                                </div>
                            </div>
                            )}
                            {emp.license && (
                            <div className="flex items-center gap-4 bg-[rgba(255,255,255,0.04)] rounded-xl p-5 border border-[rgba(255,255,255,0.06)]">
                                <div className="w-12 h-12 rounded-xl bg-gold-bg flex items-center justify-center shrink-0">
                                    <span className="text-gold text-sm font-bold">NV</span>
                                </div>
                                <div>
                                    <p className="text-[rgba(255,255,255,0.4)] text-xs uppercase tracking-wider">{t('employee.licenseLabel')}</p>
                                    <p className="text-white text-sm mt-0.5">{emp.license}</p>
                                </div>
                            </div>
                            )}
                        </div>
                    </div>
                    )}
                </div>
                <Footer />
            </div>
        </div>
    )
}
